import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import PolylineIcon from '@mui/icons-material/Polyline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { colorTokens } from '@eruvaka/web-pondlogs-base';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { activatePond } from '../../api/pondsApi';
import { fetchUserDevices, assignPondMothers, assignPondGuards, assignShrimpTalks, } from '../../api/devicesApi';
// ── Shared layout helpers ─────────────────────────────────────────────────────
const FieldRow = ({ children }) => (_jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }, children: children }));
const Field = ({ label, children }) => (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.5, display: 'block' }, children: label }), children] }));
const inputSx = {
    width: '100%',
    px: 1.25,
    py: 0.85,
    borderRadius: '10px',
    fontSize: 12,
    border: `1px solid ${colorTokens.gray200}`,
    outline: 'none',
    color: colorTokens.gray800,
    bgcolor: colorTokens.white,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    '&:focus': { borderColor: colorTokens.primary },
};
const secondaryButtonSx = {
    px: 2,
    py: 1,
    borderRadius: '10px',
    border: `1px solid ${colorTokens.gray200}`,
    bgcolor: 'white',
    color: colorTokens.gray600,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: 'pointer',
};
const primaryButtonSx = {
    px: 2.5,
    py: 1,
    borderRadius: '10px',
    border: 'none',
    color: 'white',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
};
const DeviceMultiSelect = ({ label, abbr, accentColor, items, selectedCodes, onToggle, }) => {
    const { t } = useTranslation('main');
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const triggerRef = useRef(null);
    // Close when clicking outside — but skip if the click is on the trigger itself
    // (the trigger's own onClick handles the toggle in that case)
    const handleClickAway = useCallback((e) => {
        if (triggerRef.current?.contains(e.target))
            return;
        setAnchorEl(null);
    }, []);
    const triggerLabel = selectedCodes.length === 0
        ? t('onboarding.addDevices.selectPlaceholder', { title: label })
        : `${selectedCodes.length} ${t('onboarding.addDevices.selectedLabel')} — ${selectedCodes.join(', ')}`;
    return (_jsxs(Box, { sx: { bgcolor: colorTokens.gray50, borderRadius: '10px', p: 1.5 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 700, color: colorTokens.gray800 }, children: label }), _jsx(Box, { sx: {
                            px: 0.75,
                            py: '1px',
                            borderRadius: '4px',
                            bgcolor: `${accentColor}15`,
                            border: `1px solid ${accentColor}25`,
                        }, children: _jsx(Typography, { sx: { fontSize: 9, fontWeight: 800, color: accentColor, lineHeight: 1.4 }, children: abbr }) }), _jsx(Typography, { sx: {
                            fontSize: 10,
                            fontWeight: 700,
                            color: selectedCodes.length ? accentColor : colorTokens.gray400,
                            ml: 'auto',
                        }, children: t('onboarding.addDevices.selectedCount', { count: selectedCodes.length }) })] }), items.length === 0 ? (_jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray500, px: 0.25 }, children: t('onboarding.addDevices.selectDevices') })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { ref: triggerRef, component: "button", type: "button", onClick: () => setAnchorEl(open ? null : triggerRef.current), sx: {
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.25,
                            py: 0.875,
                            borderRadius: '7px',
                            border: `1.5px solid ${open ? accentColor : colorTokens.gray200}`,
                            bgcolor: 'white',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            transition: 'border-color 0.15s',
                        }, children: [_jsx(Typography, { sx: {
                                    fontSize: 11,
                                    color: selectedCodes.length ? colorTokens.gray800 : colorTokens.gray400,
                                    fontWeight: selectedCodes.length ? 600 : 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '90%',
                                    textAlign: 'left',
                                }, children: triggerLabel }), _jsx(ChevronRightIcon, { sx: {
                                    fontSize: 13,
                                    color: colorTokens.gray400,
                                    flexShrink: 0,
                                    transform: open ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 0.15s',
                                } })] }), _jsx(Popper, { open: open, anchorEl: anchorEl, placement: "bottom-start", style: { zIndex: 1400, width: anchorEl?.offsetWidth }, children: _jsx(ClickAwayListener, { onClickAway: handleClickAway, mouseEvent: "onMouseDown", children: _jsx(Paper, { elevation: 4, sx: {
                                    borderRadius: '9px',
                                    border: `1.5px solid ${accentColor}30`,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                }, children: items.map((item, i) => {
                                    const checked = selectedCodes.includes(item.code);
                                    return (_jsxs(Box, { component: "label", sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.125,
                                            px: 1.5,
                                            py: 0.875,
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            bgcolor: checked ? `${accentColor}08` : 'transparent',
                                            borderBottom: i < items.length - 1 ? `1px solid ${colorTokens.gray100}` : 'none',
                                            '&:hover': { bgcolor: checked ? `${accentColor}12` : colorTokens.gray50 },
                                        }, children: [_jsx(Box, { component: "input", type: "checkbox", checked: checked, onChange: () => onToggle(item.code), sx: {
                                                    accentColor,
                                                    width: 14,
                                                    height: 14,
                                                    flexShrink: 0,
                                                    cursor: 'pointer',
                                                } }), _jsx(Typography, { sx: {
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: checked ? accentColor : colorTokens.gray700,
                                                    flex: 1,
                                                }, children: item.code })] }, item._id));
                                }) }) }) })] })), selectedCodes.length > 0 && (_jsx(Box, { sx: { mt: 0.875, display: 'flex', flexWrap: 'wrap', gap: 0.5 }, children: selectedCodes.map((code) => (_jsxs(Box, { sx: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.375,
                        fontSize: 10,
                        fontWeight: 700,
                        px: 0.875,
                        py: '2px',
                        borderRadius: '5px',
                        bgcolor: `${accentColor}14`,
                        color: accentColor,
                        border: `1px solid ${accentColor}30`,
                    }, children: [code, _jsx(Box, { component: "button", type: "button", onClick: () => onToggle(code), sx: {
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: accentColor,
                                p: 0,
                                display: 'flex',
                                lineHeight: 1,
                                opacity: 0.6,
                                '&:hover': { opacity: 1 },
                            }, children: _jsx(CloseIcon, { sx: { fontSize: 9 } }) })] }, code))) }))] }));
};
export const DrawPondPanel = (props) => {
    const { coordinates, areaHa, locationOptions, defaultLocationName, onResolveLocationId, isDrawing, onStartDraw, onSave, onCancel, } = props;
    const { t } = useTranslation(['main', 'ponds']);
    const [pondName, setPondName] = useState('');
    const [locationName, setLocationName] = useState(defaultLocationName ?? '');
    const [pondSize, setPondSize] = useState('');
    const [units, setUnits] = useState('ha');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [pondNameError, setPondNameError] = useState(null);
    const [locationNameError, setLocationNameError] = useState(null);
    const [pondSizeError, setPondSizeError] = useState(null);
    // Shared
    const [step, setStep] = useState('details');
    const [createdPond, setCreatedPond] = useState(null);
    // Step 2 — activation
    const [actStockingDate, setActStockingDate] = useState('');
    const [actPlsStocked, setActPlsStocked] = useState('');
    const [actSpecies, setActSpecies] = useState('Vannamei');
    const [actInitialAbw, setActInitialAbw] = useState('');
    const [actHatcheryName, setActHatcheryName] = useState('');
    const [actAeratorHp, setActAeratorHp] = useState('');
    const [actFieldErrors, setActFieldErrors] = useState({});
    const [isActivating, setIsActivating] = useState(false);
    const [activationError, setActivationError] = useState(null);
    // Step 3 — devices
    const [availableDevices, setAvailableDevices] = useState(null);
    const [selectedPmCodes, setSelectedPmCodes] = useState([]);
    const [selectedPgCodes, setSelectedPgCodes] = useState([]);
    const [selectedStCodes, setSelectedStCodes] = useState([]);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [isSavingDevices, setIsSavingDevices] = useState(false);
    const [devicesError, setDevicesError] = useState(null);
    // ── Effects ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (defaultLocationName)
            setLocationName(defaultLocationName);
    }, [defaultLocationName]);
    useEffect(() => {
        if (areaHa != null)
            setPondSize(areaHa.toFixed(2));
    }, [areaHa]);
    useEffect(() => {
        if (step !== 'devices' || !createdPond)
            return;
        setIsLoadingDevices(true);
        setDevicesError(null);
        fetchUserDevices(['POND_MOTHER', 'POND_GUARD', 'SHRIMP_TALK'])
            .then((devices) => setAvailableDevices(devices))
            .catch(() => setDevicesError(t('onboarding.drawPanel.errorLoadDevicesFailed')))
            .finally(() => setIsLoadingDevices(false));
    }, [step, createdPond]);
    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleUnitsChange = (nextUnits) => {
        if (nextUnits === units)
            return;
        const current = Number(pondSize);
        if (Number.isFinite(current) && current > 0) {
            const converted = units === 'ha' && nextUnits === 'ac'
                ? current * 2.471
                : units === 'ac' && nextUnits === 'ha'
                    ? current / 2.471
                    : current;
            setPondSize(converted.toFixed(2));
        }
        setUnits(nextUnits);
    };
    const handleCreatePond = async () => {
        // Field-level validation
        let valid = true;
        if (!pondName.trim()) {
            setPondNameError('onboarding.createPonds.errorNameRequired');
            valid = false;
        }
        else {
            setPondNameError(null);
        }
        if (!locationName.trim()) {
            setLocationNameError('onboarding.createPonds.errorLocationRequired');
            valid = false;
        }
        else {
            setLocationNameError(null);
        }
        if (!pondSize.trim()) {
            setPondSizeError('onboarding.createPonds.errorSizeRequired');
            valid = false;
        }
        else {
            setPondSizeError(null);
        }
        if (!coordinates) {
            setError('onboarding.createPonds.errorDrawRequired');
            valid = false;
        }
        if (!valid)
            return;
        // TypeScript narrowing: coordinates guaranteed non-null (checked above)
        if (!coordinates)
            return;
        const parsedSize = Number(pondSize);
        if (!Number.isFinite(parsedSize) || parsedSize <= 0) {
            setPondSizeError('onboarding.createPonds.errorSizeInvalid');
            return;
        }
        setIsSaving(true);
        setError(null);
        setPondNameError(null);
        try {
            const locationId = await onResolveLocationId(locationName.trim());
            const pond = await onSave({
                title: pondName.trim(),
                pond_coordinates: coordinates,
                timezone: { name: 'Asia/Kolkata', offset: 5.5 },
                location_id: locationId,
                size: parsedSize,
                units,
            });
            setCreatedPond(pond);
            setStep('activation');
        }
        catch (e) {
            const errCode = e.errorCode ?? '';
            if (errCode === 'DUPLICATE_PONDS') {
                setPondNameError('ponds:errors.duplicateName');
            }
            else {
                setError(errCode === 'FORBIDDEN'
                    ? 'ponds:errors.permissionDenied'
                    : 'onboarding.createPonds.errorSaveFailed');
            }
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleActivate = async () => {
        const errors = {};
        if (!actStockingDate)
            errors.stockingDate = 'onboarding.activatePonds.errorStockingDateRequired';
        if (!actPlsStocked || Number(actPlsStocked) <= 0)
            errors.plsStocked = 'onboarding.activatePonds.errorPlsStockedRequired';
        if (!actInitialAbw)
            errors.initialAbw = 'onboarding.activatePonds.errorInitialAbwRequired';
        else if (isNaN(Number(actInitialAbw)) || Number(actInitialAbw) <= 0)
            errors.initialAbw = 'onboarding.activatePonds.errorInitialAbwInvalid';
        if (Object.keys(errors).length > 0) {
            setActFieldErrors(errors);
            return;
        }
        setActFieldErrors({});
        if (!createdPond)
            return;
        setIsActivating(true);
        setActivationError(null);
        try {
            await activatePond(createdPond.id, {
                stockingDate: new Date(actStockingDate).toISOString(),
                plsStocked: Number(actPlsStocked),
                speciesType: actSpecies,
                initialAbw: actInitialAbw ? Number(actInitialAbw) : undefined,
                hatcheryName: actHatcheryName || undefined,
                aeratorHp: actAeratorHp ? Number(actAeratorHp) : undefined,
            });
            setStep('devices');
        }
        catch (e) {
            setActivationError('onboarding.activatePonds.errorActivationFailed');
        }
        finally {
            setIsActivating(false);
        }
    };
    const handleSaveDevices = async () => {
        if (!createdPond || !availableDevices) {
            onCancel();
            return;
        }
        setIsSavingDevices(true);
        setDevicesError(null);
        try {
            const pmAssignments = availableDevices.pond_mothers
                .filter((pm) => selectedPmCodes.includes(pm.code))
                .map((pm) => ({ pond_id: createdPond.id, pond_mother_id: pm._id, title: pm.code }));
            const pgAssignments = availableDevices.pond_guards
                .filter((pg) => selectedPgCodes.includes(pg.code))
                .map((pg) => ({ pond_id: createdPond.id, pond_guard_id: pg._id, title: pg.code }));
            const stAssignments = availableDevices.shrimp_talks
                .filter((st) => selectedStCodes.includes(st.code))
                .map((st) => ({
                pond_id: createdPond.id,
                shrimp_talk_id: st._id,
                title: st.code,
                settings: { feed_limit: 0 },
            }));
            if (pmAssignments.length > 0)
                await assignPondMothers(pmAssignments);
            if (pgAssignments.length > 0)
                await assignPondGuards(pgAssignments);
            if (stAssignments.length > 0)
                await assignShrimpTalks(stAssignments);
            onCancel();
        }
        catch (e) {
            setDevicesError('onboarding.drawPanel.errorSaveDevicesFailed');
        }
        finally {
            setIsSavingDevices(false);
        }
    };
    // ── Derived ───────────────────────────────────────────────────────────────
    const hasPolygon = coordinates !== null;
    // Button is only disabled during the async save call — validation is shown inline on submit
    const isCreateDisabled = isSaving || step !== 'details';
    const stepLabel = step === 'details'
        ? t('onboarding.drawPanel.step1')
        : step === 'activation'
            ? t('onboarding.drawPanel.step2')
            : t('onboarding.drawPanel.step3');
    // ── Render ────────────────────────────────────────────────────────────────
    return (_jsxs(Box, { sx: {
            width: 460,
            maxWidth: 'calc(100vw - 24px)',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 25px 50px -12px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '82vh',
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.25,
                    py: 1.5,
                    bgcolor: colorTokens.gray50,
                    borderBottom: `1px solid ${colorTokens.gray200}`,
                    flexShrink: 0,
                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 800, color: colorTokens.primary }, children: t('onboarding.drawPanel.title') }), hasPolygon && (_jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600 }, children: stepLabel }))] }), hasPolygon && (_jsx(Box, { component: "button", type: "button", onClick: onCancel, sx: {
                            p: 0.75,
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: 1,
                            display: 'flex',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                        }, children: _jsx(Typography, { sx: { fontSize: 15, color: colorTokens.gray600, lineHeight: 1 }, children: "\u00D7" }) }))] }), _jsx(Box, { sx: {
                    p: 2.25,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    flex: 1,
                }, children: !hasPolygon ? (_jsxs(Box, { sx: { p: 2.25 }, children: [_jsx(Typography, { sx: { fontSize: 12, color: colorTokens.gray600, mb: 1.5 }, children: isDrawing
                                ? t('onboarding.drawPanel.drawingHint')
                                : t('onboarding.drawPanel.drawHint') }), _jsx(Button, { variant: isDrawing ? 'outlined' : 'contained', size: "small", startIcon: _jsx(PolylineIcon, {}), onClick: onStartDraw, disabled: isDrawing, sx: { alignSelf: 'flex-start', textTransform: 'none' }, children: isDrawing
                                ? t('onboarding.drawPanel.drawingButton')
                                : t('onboarding.drawPanel.startDrawingButton') })] })) : step === 'details' ? (
                /* ── Step 1: Pond details ── */
                _jsxs(_Fragment, { children: [_jsxs(FieldRow, { children: [_jsxs(Field, { label: t('onboarding.createPonds.pondName'), children: [_jsx(Box, { component: "input", value: pondName, onChange: (e) => {
                                                setPondName(e.target.value);
                                                if (pondNameError)
                                                    setPondNameError(null);
                                            }, sx: {
                                                ...inputSx,
                                                borderColor: pondNameError ? colorTokens.error : undefined,
                                            }, autoFocus: true }), pondNameError && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(pondNameError) }))] }), _jsxs(Field, { label: t('onboarding.createPonds.location'), children: [_jsxs(Box, { component: "select", value: locationName, onChange: (e) => {
                                                setLocationName(e.target.value);
                                                if (locationNameError)
                                                    setLocationNameError(null);
                                            }, sx: {
                                                ...inputSx,
                                                appearance: 'auto',
                                                cursor: 'pointer',
                                                color: locationName ? colorTokens.gray800 : colorTokens.gray500,
                                                borderColor: locationNameError ? colorTokens.error : undefined,
                                            }, children: [_jsx("option", { value: "", disabled: true, children: t('onboarding.createPonds.locationSelectPlaceholder') }), locationOptions.map((location) => (_jsx("option", { value: location.name, children: location.name }, location.id))), locationName &&
                                                    !locationOptions.some((l) => l.name.toLowerCase().trim() === locationName.toLowerCase().trim()) && (_jsxs("option", { value: locationName, children: [locationName, " ", t('onboarding.createPonds.locationNewSuffix')] }))] }), locationNameError && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(locationNameError) }))] })] }), _jsxs(FieldRow, { children: [_jsxs(Field, { label: t('onboarding.createPonds.pondSizeLabel'), children: [_jsx(Box, { component: "input", type: "number", value: pondSize, onChange: (e) => {
                                                setPondSize(e.target.value);
                                                if (pondSizeError)
                                                    setPondSizeError(null);
                                            }, min: 0.01, step: 0.01, sx: {
                                                ...inputSx,
                                                borderColor: pondSizeError ? colorTokens.error : undefined,
                                            } }), pondSizeError && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(pondSizeError) }))] }), _jsx(Field, { label: t('onboarding.createPonds.unitsLabel'), children: _jsxs(Box, { component: "select", value: units, onChange: (e) => handleUnitsChange(e.target.value), sx: { ...inputSx, appearance: 'auto' }, children: [_jsx("option", { value: "ha", children: "ha" }), _jsx("option", { value: "ac", children: "ac" })] }) })] }), error && (_jsx(Box, { sx: {
                                p: 1.25,
                                borderRadius: '10px',
                                bgcolor: `${colorTokens.error}10`,
                                border: `1px solid ${colorTokens.error}30`,
                            }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.error }, children: t(error) }) }))] })) : step === 'activation' ? (
                /* ── Step 2: Activation ── */
                _jsxs(_Fragment, { children: [_jsxs(FieldRow, { children: [_jsx(Field, { label: t('onboarding.activatePonds.pondSizeHaLabel'), children: _jsx(Box, { component: "input", readOnly: true, value: createdPond?.areaHectares.toFixed(2) ?? '', sx: { ...inputSx, bgcolor: colorTokens.gray50, color: colorTokens.gray500 } }) }), _jsx(Field, { label: t('onboarding.activatePonds.species'), children: _jsxs(Box, { component: "select", value: actSpecies, onChange: (e) => setActSpecies(e.target.value), sx: { ...inputSx, appearance: 'auto' }, children: [_jsx("option", { value: "Vannamei", children: t('onboarding.drawPanel.speciesVannamei') }), _jsx("option", { value: "Monodon", children: t('onboarding.drawPanel.speciesMonodon') }), _jsx("option", { value: "Fish", children: t('onboarding.drawPanel.speciesFish') }), _jsx("option", { value: "Other", children: t('onboarding.drawPanel.speciesOther') })] }) })] }), _jsxs(FieldRow, { children: [_jsxs(Field, { label: `${t('onboarding.activatePonds.stockingDate')} *`, children: [_jsx(Box, { component: "input", type: "date", value: actStockingDate, onChange: (e) => {
                                                setActStockingDate(e.target.value);
                                                if (actFieldErrors.stockingDate)
                                                    setActFieldErrors((p) => ({ ...p, stockingDate: undefined }));
                                            }, max: new Date().toISOString().split('T')[0], sx: {
                                                ...inputSx,
                                                borderColor: actFieldErrors.stockingDate ? colorTokens.error : undefined,
                                            } }), actFieldErrors.stockingDate && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(actFieldErrors.stockingDate) }))] }), _jsxs(Field, { label: `${t('onboarding.activatePonds.plsStocked')} *`, children: [_jsx(Box, { component: "input", type: "number", placeholder: "e.g. 600000", value: actPlsStocked, onChange: (e) => {
                                                setActPlsStocked(e.target.value);
                                                if (actFieldErrors.plsStocked)
                                                    setActFieldErrors((p) => ({ ...p, plsStocked: undefined }));
                                            }, min: 0, sx: {
                                                ...inputSx,
                                                borderColor: actFieldErrors.plsStocked ? colorTokens.error : undefined,
                                            } }), actFieldErrors.plsStocked && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(actFieldErrors.plsStocked) }))] })] }), _jsxs(FieldRow, { children: [_jsxs(Field, { label: t('onboarding.activatePonds.initialAbwLabel'), children: [_jsx(Box, { component: "input", type: "number", placeholder: "0.1", value: actInitialAbw, onChange: (e) => {
                                                setActInitialAbw(e.target.value);
                                                if (actFieldErrors.initialAbw)
                                                    setActFieldErrors((p) => ({ ...p, initialAbw: undefined }));
                                            }, min: 0, step: 0.01, sx: {
                                                ...inputSx,
                                                borderColor: actFieldErrors.initialAbw ? colorTokens.error : undefined,
                                            } }), actFieldErrors.initialAbw && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(actFieldErrors.initialAbw) }))] }), _jsx(Field, { label: t('onboarding.activatePonds.aeratorHp'), children: _jsx(Box, { component: "input", type: "number", placeholder: "e.g. 5", value: actAeratorHp, onChange: (e) => setActAeratorHp(e.target.value), min: 0, step: 0.1, sx: inputSx }) })] }), _jsx(Field, { label: t('onboarding.activatePonds.hatcheryName'), children: _jsx(Box, { component: "input", type: "text", placeholder: "e.g. Coastal Hatchery", value: actHatcheryName, onChange: (e) => setActHatcheryName(e.target.value), sx: inputSx }) }), activationError && (_jsx(Box, { sx: {
                                p: 1.25,
                                borderRadius: '10px',
                                bgcolor: `${colorTokens.error}10`,
                                border: `1px solid ${colorTokens.error}30`,
                            }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.error }, children: t(activationError) }) }))] })) : (
                /* ── Step 3: Assign devices ── */
                _jsxs(_Fragment, { children: [createdPond && (_jsxs(Box, { sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 0.625,
                                borderRadius: '20px',
                                bgcolor: `${colorTokens.primary}0c`,
                                border: `1px solid ${colorTokens.primary}22`,
                                alignSelf: 'flex-start',
                            }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 700, color: colorTokens.primary }, children: createdPond.name }), createdPond.areaHectares > 0 && (_jsxs(Typography, { sx: { fontSize: 10, color: colorTokens.gray500 }, children: ["\u00B7 ", createdPond.areaHectares.toFixed(2), " ha"] }))] })), _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.gray600 }, children: t('onboarding.addDevices.subtitle') }), devicesError && (_jsx(Box, { sx: {
                                p: 1.25,
                                borderRadius: '10px',
                                bgcolor: `${colorTokens.error}10`,
                                border: `1px solid ${colorTokens.error}30`,
                            }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.error }, children: devicesError }) })), isLoadingDevices ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 2.5 }, children: _jsx(CircularProgress, { size: 20 }) })) : (
                        // Always render rows — items default to [] on error so user sees empty state
                        _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.25 }, children: [_jsx(DeviceMultiSelect, { label: t('onboarding.addDevices.pondMother'), abbr: "PM", accentColor: colorTokens.primary, items: availableDevices?.pond_mothers ?? [], selectedCodes: selectedPmCodes, onToggle: (code) => setSelectedPmCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]) }), _jsx(DeviceMultiSelect, { label: t('onboarding.addDevices.pondGuard'), abbr: "PG", accentColor: "#00ACA9", items: availableDevices?.pond_guards ?? [], selectedCodes: selectedPgCodes, onToggle: (code) => setSelectedPgCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]) }), _jsx(DeviceMultiSelect, { label: t('onboarding.addDevices.shrimpTalk'), abbr: "ST", accentColor: "#C70D6D", items: availableDevices?.shrimp_talks ?? [], selectedCodes: selectedStCodes, onToggle: (code) => setSelectedStCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]) })] }))] })) }), _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    px: 2.25,
                    py: 1.5,
                    borderTop: `1px solid ${colorTokens.gray200}`,
                    bgcolor: colorTokens.gray50,
                    flexShrink: 0,
                }, children: [_jsx(Box, { component: "button", type: "button", onClick: !hasPolygon || step === 'details'
                            ? onCancel
                            : step === 'activation'
                                ? () => setStep('details')
                                : () => setStep('activation'), sx: secondaryButtonSx, children: !hasPolygon || step === 'details'
                            ? t('onboarding.drawPanel.cancelButton')
                            : t('onboarding.drawPanel.backButton') }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [hasPolygon && (step === 'activation' || step === 'devices') && (_jsx(Box, { component: "button", type: "button", onClick: () => {
                                    if (step === 'activation')
                                        setStep('devices');
                                    else
                                        onCancel();
                                }, sx: secondaryButtonSx, children: t('onboarding.drawPanel.skipButton') })), _jsxs(Box, { component: "button", type: "button", onClick: () => {
                                    if (!hasPolygon) {
                                        onStartDraw();
                                        return;
                                    }
                                    if (step === 'details') {
                                        void handleCreatePond();
                                        return;
                                    }
                                    if (step === 'activation') {
                                        void handleActivate();
                                        return;
                                    }
                                    void handleSaveDevices();
                                }, disabled: !hasPolygon
                                    ? isDrawing
                                    : step === 'details'
                                        ? isCreateDisabled
                                        : step === 'activation'
                                            ? isActivating
                                            : isSavingDevices, sx: {
                                    ...primaryButtonSx,
                                    bgcolor: (!hasPolygon && isDrawing) ||
                                        (step === 'details' && isCreateDisabled) ||
                                        (step === 'activation' && isActivating) ||
                                        (step === 'devices' && isSavingDevices)
                                        ? colorTokens.gray300
                                        : colorTokens.primary,
                                    cursor: (!hasPolygon && isDrawing) ||
                                        (step === 'details' && isCreateDisabled) ||
                                        (step === 'activation' && isActivating) ||
                                        (step === 'devices' && isSavingDevices)
                                        ? 'not-allowed'
                                        : 'pointer',
                                }, children: [(isSaving || isActivating || isSavingDevices) && (_jsx(CircularProgress, { size: 11, sx: { color: 'white' } })), !hasPolygon
                                        ? isDrawing
                                            ? t('onboarding.drawPanel.drawingButton')
                                            : t('onboarding.drawPanel.startDrawingButton')
                                        : step === 'details'
                                            ? isSaving
                                                ? t('onboarding.common.savingLabel')
                                                : t('onboarding.common.savePondButton')
                                            : step === 'activation'
                                                ? isActivating
                                                    ? t('onboarding.common.activatingLabel')
                                                    : t('onboarding.activatePonds.activateButton')
                                                : isSavingDevices
                                                    ? t('onboarding.drawPanel.savingDevicesLabel')
                                                    : t('onboarding.addDevices.saveButton')] })] })] })] }));
};
